import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Plus, UserCog } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function UsersPage() {
  const { getAuthHeaders } = useAuth();
  const [users, setUsers] = useState([]);
  const [branches, setBranches] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ username: '', password: '', name: '', role: 'staff', branches: [] });
  const [editId, setEditId] = useState(null);

  useEffect(() => { fetchUsers(); fetchBranches(); }, []);

  const fetchUsers = async () => {
    try {
      const res = await axios.get(`${API}/api/users`, { headers: getAuthHeaders() });
      setUsers(res.data);
    } catch (err) { console.error(err); }
  };

  const fetchBranches = async () => {
    try {
      const res = await axios.get(`${API}/api/branches`, { headers: getAuthHeaders() });
      setBranches(res.data);
    } catch (err) { console.error(err); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editId) {
        await axios.put(`${API}/api/users/${editId}`, form, { headers: getAuthHeaders() });
      } else {
        await axios.post(`${API}/api/users`, form, { headers: getAuthHeaders() });
      }
      setShowForm(false);
      setForm({ username: '', password: '', name: '', role: 'staff', branches: [] });
      setEditId(null);
      fetchUsers();
    } catch (err) { console.error(err); }
  };

  const editUser = (u) => {
    setForm({ username: u.username, password: '', name: u.name, role: u.role, branches: u.branches || [] });
    setEditId(u.id);
    setShowForm(true);
  };

  const deleteUser = async (id) => {
    if (!window.confirm('Eliminar este usuario?')) return;
    try {
      await axios.delete(`${API}/api/users/${id}`, { headers: getAuthHeaders() });
      fetchUsers();
    } catch (err) { console.error(err); }
  };

  return (
    <div data-testid="users-page">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-[#1D3C6E] font-['Outfit']">Usuarios</h1>
        <Button onClick={() => { setEditId(null); setForm({ username: '', password: '', name: '', role: 'staff', branches: [] }); setShowForm(true); }} className="bg-[#1D3C6E] hover:bg-[#152B50]" data-testid="new-user-button">
          <Plus className="w-4 h-4 mr-2" /> Nuevo Usuario
        </Button>
      </div>

      <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="text-left px-4 py-3 font-medium text-slate-600">Nombre</th>
              <th className="text-left px-4 py-3 font-medium text-slate-600">Usuario</th>
              <th className="text-left px-4 py-3 font-medium text-slate-600">Rol</th>
              <th className="text-left px-4 py-3 font-medium text-slate-600">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id || u.username} className="border-b border-slate-100 hover:bg-slate-50">
                <td className="px-4 py-3 font-medium flex items-center gap-2">
                  <UserCog className="w-4 h-4 text-slate-400" />{u.name}
                </td>
                <td className="px-4 py-3 text-slate-600">{u.username}</td>
                <td className="px-4 py-3">
                  <span className={`status-badge ${u.role === 'admin' ? 'bg-[#1D3C6E]/10 text-[#1D3C6E]' : 'bg-slate-100 text-slate-600'}`}>
                    {u.role === 'admin' ? 'Admin' : 'Staff'}
                  </span>
                </td>
                <td className="px-4 py-3">
                  {u.role !== 'admin' && (
                    <div className="flex gap-2">
                      <button onClick={() => editUser(u)} className="text-xs text-[#1D3C6E] hover:underline">Editar</button>
                      <button onClick={() => deleteUser(u.id)} className="text-xs text-red-500 hover:underline">Eliminar</button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent>
          <DialogHeader><DialogTitle className="text-[#1D3C6E] font-['Outfit']">{editId ? 'Editar' : 'Nuevo'} Usuario</DialogTitle></DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-3">
            {!editId && <div><label className="text-xs font-medium text-slate-600">Usuario *</label><Input data-testid="user-username-input" value={form.username} onChange={(e) => setForm({...form, username: e.target.value})} required={!editId} /></div>}
            <div><label className="text-xs font-medium text-slate-600">Nombre *</label><Input value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} required /></div>
            <div><label className="text-xs font-medium text-slate-600">{editId ? 'Nueva contrasena (dejar vacio para no cambiar)' : 'Contrasena *'}</label><Input type="password" value={form.password} onChange={(e) => setForm({...form, password: e.target.value})} required={!editId} /></div>
            <div>
              <label className="text-xs font-medium text-slate-600">Rol</label>
              <Select value={form.role} onValueChange={(val) => setForm({...form, role: val})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="staff">Staff</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button type="submit" className="w-full bg-[#1D3C6E] hover:bg-[#152B50]" data-testid="submit-user-button">Guardar</Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
